import React, { useState } from "react";
import { Modal, Dropdown } from "react-bootstrap";
import "../styles.css";
import { instance } from "../../../utils/utils";
import InlineModal from "../../info-modal/infoModal";
import { getBackendError } from "../../../utils/error-helpers";
import { MODAL_CONFIGS } from "./modal-configs";

const TAX_REGIME_OPTIONS = [
    { value: 10, label: "01 - IVA" },
    { value: 20, label: "04 - INC" },
    { value: 30, label: "ZA - IVA e INC" },
    { value: 40, label: "ZZ - No aplica" }
];

const TAX_RESPONSIBILITY_OPTIONS = [
    { value: 10, label: "O-13 Gran Contribuyente" },
    { value: 20, label: "O-15 Autorretenedor" },
    { value: 30, label: "O-23 Agente de retención IVA" },
    { value: 40, label: "O-47 Régimen simple de tributación" },
    { value: 50, label: "R-99-PN No aplica - Otros" }
];

const DOCUMENT_TYPES = [
    { label: "Pasaporte", value: 1 },
    { label: "Licencia de conducción", value: 2 },
    { label: "Seguridad social", value: 3 },
    { label: "Cédula de Ciudadanía", value: 4 },
    { label: "Cédula de Extranjería", value: 5 },
    { label: "NIT", value: 6 },
    { label: "Documento Nacional de Identificación", value: 68 },
    { label: "Tarjeta de Identidad", value: 100 },
];

const ActivateTrialModal = ({ show, isTrial, selectedPlan, onCompleted, onBack, onCancel }) => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        documentType: null,
        documentNumber: "",
        cardNumber: "",
        expiration: "",
        cvc: "",
        saveCard: false,
        showInvoice: false,
        nit: "",
        fiscalRegime: null,
        fiscalResponsibility: null,
        invoiceEmail: "",
        couponCode: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [couponResult, setCouponResult] = useState(null);
    const [couponError, setCouponError] = useState("");
    const [infoModal, setInfoModal] = useState(null);
    const [activeModal, setActiveModal] = useState(null);
    const [modalContext, setModalContext] = useState({});

    const closeModal = () => setActiveModal(null);

    const getOrderedPrice = () => {
        if (!couponResult) return selectedPlan.totalPrice;
        const discount = (selectedPlan.totalPriceNumeric * Number(couponResult.percentage)) / 100;
        const final = selectedPlan.totalPriceNumeric - discount;
        return `$${final.toLocaleString("es-CO")} COP`;
    };

    const displayPrice = getOrderedPrice();

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));
    };

    const handleExpirationChange = (e) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 4) value = value.slice(0, 4);

        if (value.length >= 2 && e.nativeEvent.inputType !== 'deleteContentBackward') {
            value = `${value.slice(0, 2)}/${value.slice(2)}`;
        }

        handleInputChange("expiration", value);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = "El nombre es requerido.";
        } else if (formData.firstName.trim().length <= 3) {
            newErrors.firstName = "El nombre debe tener más de 3 caracteres.";
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = "El apellido es requerido.";
        } else if (formData.lastName.trim().length <= 3) {
            newErrors.lastName = "El apellido debe tener más de 3 caracteres.";
        }

        if (!formData.documentType) newErrors.documentType = "Tipo de documento es requerido.";

        if (!formData.documentNumber.trim()) {
            newErrors.documentNumber = "Número de documento es requerido.";
        } else if (formData.documentNumber.trim().length <= 3) {
            newErrors.documentNumber = "El documento debe tener más de 3 caracteres.";
        }
        if (!formData.cardNumber.trim()) newErrors.cardNumber = "Número de tarjeta es requerido.";
        if (!formData.expiration.trim()) newErrors.expiration = "Fecha de vencimiento es requerida.";
        if (!formData.cvc.trim()) newErrors.cvc = "CVC es requerido.";

        if (formData.showInvoice) {
            if (!formData.nit.trim()) newErrors.nit = "NIT es requerido para factura.";
            if (formData.fiscalRegime === null) newErrors.fiscalRegime = "Régimen fiscal es requerido.";
            if (formData.fiscalResponsibility === null) newErrors.fiscalResponsibility = "Responsabilidad fiscal es requerida.";
            if (!formData.invoiceEmail.trim()) newErrors.invoiceEmail = "Correo para facturación es requerido.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const payload = {
                want_electronic_invoice: formData.showInvoice,
                nit_id_number: formData.nit || null,
                tax_regime: formData.fiscalRegime,
                tax_responsibility: formData.fiscalResponsibility,
                billing_email: formData.invoiceEmail || null,

                plan_id: selectedPlan.backendPlan.id,
                cycle: selectedPlan.billingPeriod === "yearly" ? 20 : 10,
                extra_clients: selectedPlan.config.extraUsers || 0,
                extra_role_user_admins: selectedPlan.config.extraAdmins || 0,
                coupon: formData.couponCode || null,

                cardholder_name: formData.firstName,
                cardholder_last_name: formData.lastName,
                identification_type: formData.documentType,
                identification: formData.documentNumber,
                card_number: formData.cardNumber,
                cvv: formData.cvc,
                expiration: formData.expiration,
                skip_trial: !isTrial,
            };

            await instance.post(
                "/customers/company/plan/create/onboarding",
                payload
            );

            onCompleted();
        } catch (error) {
            console.error("Error processing payment:", error);

            const backendError = error?.response?.data;

            if (backendError?.error_payment) {
                setModalContext({
                    summary: {}
                });
                setActiveModal("PAYMENT_REJECTED_UPDATE");
                return;
            }

            const errorMessage = getBackendError(error);
            setInfoModal({
                iconType: "error",
                title: "Error al procesar el pago",
                description: errorMessage,
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => setInfoModal(null),
                },
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApplyCoupon = async () => {
        if (!formData.couponCode.trim()) {
            setCouponError("Debes ingresar un código.");
            return;
        }

        try {
            const res = await instance.post("/customers/company/apply-coupon", {
                coupon: formData.couponCode,
                planId: selectedPlan?.id
            });
            setCouponResult(res.data);
            setCouponError("");
        } catch (error) {
            console.error(error);
            setCouponResult(null);
            setCouponError("El cupón ingresado no es válido o ha expirado.");
        }
    };

    const totalUsers =
        selectedPlan?.backendPlan?.clients_limit +
        (selectedPlan?.config?.extraUsers || 0);

    const totalAdmins =
        selectedPlan?.backendPlan?.role_user_limit +
        (selectedPlan?.config?.extraAdmins || 0);

    console.log(selectedPlan);

    return (
        <>
            {infoModal && <InlineModal {...infoModal} />}

            {activeModal && (
                <InlineModal
                    {...MODAL_CONFIGS[activeModal]({
                        closeModal,
                        handlePayment: handleConfirm,
                        ...modalContext
                    })}
                />
            )}

            <Modal
                show={show}
                centered
                size="xl"
                dialogClassName="trial-modal"
            >
                <Modal.Body className="trial-modal-body d-flex flex-column justify-content-center align-items-center gap-4 p-4 py-5">

                    <div className="trial-modal-header d-flex align-items-center justify-content-center w-100 position-relative py-3">
                        <button
                            className="trial-modal-close-btn btn p-0 border-0 bg-transparent position-absolute top-50 start-0 translate-middle-y ms-2"
                            onClick={onBack}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                                <path d="M25.5811 34.4609H9.88379C4.97749 34.4609 1 30.4835 1 25.5771V9.87988C1 4.97358 4.97749 0.996094 9.88379 0.996094H25.5811C30.4874 0.996094 34.4648 4.97358 34.4648 9.87988V25.5771L34.4531 26.0342C34.223 30.5768 30.5807 34.2191 26.0381 34.4492L25.5811 34.4609Z" stroke="#9DACBD" stroke-width="2" />
                                <path d="M10.9998 17.6331C10.9998 17.0369 11.2273 16.4405 11.6823 15.9855L20.7119 6.95586C21.6218 6.04596 23.0971 6.04596 24.007 6.95586C24.9169 7.86576 24.9169 9.34112 24.007 10.251L16.6249 17.6331L24.007 25.0152C24.9169 25.9251 24.9169 27.4005 24.007 28.3104C23.0971 29.2203 21.6218 29.2203 20.7119 28.3104L11.6823 19.2808C11.2273 18.8258 10.9998 18.2294 10.9998 17.6331Z" fill="#9DACBD" />
                            </svg>
                        </button>
                    </div>

                    <div className="trial-modal-content container-fluid">
                        <div className="row">
                            <div className="col-12 col-lg-7 d-flex flex-column gap-4 p-4 pt-0">
                                <div className="trial-modal-card">
                                    <h5 className="trial-modal-card-title mb-0">
                                        <svg className="me-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M11.2119 2.25C11.715 2.03439 12.285 2.03439 12.7881 2.25L18.7119 4.78906C19.5218 5.13617 20.0094 5.97195 19.9121 6.84766L19.2607 12.71C19.0611 14.5068 18.174 16.157 16.7852 17.3145L13.2803 20.2344C12.5386 20.8525 11.4614 20.8525 10.7197 20.2344L7.21484 17.3145C5.82601 16.157 4.93892 14.5068 4.73926 12.71L4.08789 6.84766C3.99065 5.97195 4.47825 5.13617 5.28809 4.78906L11.2119 2.25Z" stroke="#3177BE" stroke-width="2" stroke-linecap="round" />
                                        </svg>
                                        Información del titular
                                    </h5>
                                    <div className="w-100 d-flex gap-3">
                                        <div className="flex-grow-1 w-50">
                                            <label className="form-label">Nombre</label>
                                            <input
                                                type="text"
                                                className="form-control trial-modal-input"
                                                value={formData.firstName}
                                                onChange={(e) => handleInputChange("firstName", e.target.value)}
                                            />
                                            {errors.firstName && <small className="text-danger">{errors.firstName}</small>}
                                        </div>
                                        <div className="flex-grow-1 w-50">
                                            <label className="form-label">Apellido</label>
                                            <input
                                                type="text"
                                                className="form-control trial-modal-input"
                                                value={formData.lastName}
                                                onChange={(e) => handleInputChange("lastName", e.target.value)}
                                            />
                                            {errors.lastName && <small className="text-danger">{errors.lastName}</small>}
                                        </div>
                                    </div>
                                    <div className="w-100 d-flex gap-3">
                                        <div className="flex-grow-1 w-50">
                                            <label className="form-label">Tipo de documento</label>

                                            <Dropdown className="w-100 trial-modal-select">
                                                <Dropdown.Toggle
                                                    className="w-100 d-flex justify-content-between align-items-center trial-modal-select-btn"
                                                >
                                                    {
                                                        DOCUMENT_TYPES.find(d => d.value === formData.documentType)?.label
                                                        || "Seleccionar ..."
                                                    }
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
                                                        <path d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z" fill="#9DACBD" />
                                                    </svg>
                                                </Dropdown.Toggle>

                                                <Dropdown.Menu className="trial-modal-select-menu w-100">
                                                    {DOCUMENT_TYPES.map(opt => (
                                                        <Dropdown.Item
                                                            key={opt.value}
                                                            onClick={() => handleInputChange("documentType", opt.value)}
                                                        >
                                                            {opt.label}
                                                        </Dropdown.Item>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            {errors.documentType && <small className="text-danger d-block mt-1">{errors.documentType}</small>}
                                        </div>

                                        <div className="flex-grow-1 w-50">
                                            <label className="form-label trial-modal-label">
                                                Número de documento
                                            </label>
                                            <input
                                                type="text"
                                                className="trial-modal-input form-control"
                                                placeholder="Ej. 123456789"
                                                value={formData.documentNumber}
                                                onChange={(e) => handleInputChange("documentNumber", e.target.value)}
                                            />
                                            {errors.documentNumber && <small className="text-danger">{errors.documentNumber}</small>}
                                        </div>
                                    </div>
                                </div>

                                <div className="trial-modal-card">
                                    <h5 className="trial-modal-card-title mb-0">
                                        <svg className="me-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <rect x="3" y="6" width="18" height="13" rx="2" stroke="#3177BE" strokeWidth="2" />
                                            <path d="M7 15H7.01" stroke="#3177BE" strokeWidth="2" strokeLinecap="round" />
                                            <path d="M4 11H21" stroke="#3177BE" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                        Método de pago
                                    </h5>
                                    <p className="trial-modal-card-description mb-0">
                                        <strong>¿Por qué solicitamos tu tarjeta? </strong>
                                        {isTrial
                                            ? <>Tu tarjeta es necesaria únicamente para validar tu identidad y activar tu periodo de prueba. <strong>No se realizará ningún cobro durante los primeros 30 días.</strong> Al finalizar la prueba, el plan seleccionado se activará automáticamente, a menos que canceles antes sin ningún costo.</>
                                            : "Tu tarjeta es necesaria para procesar el pago de tu suscripción. La facturación se realizará de forma segura y automática según el ciclo elegido."
                                        }
                                    </p>
                                    <div className="w-100 mb-0">
                                        <label className="form-label">Número de tarjeta</label>
                                        <div className="trial-modal-input-icon position-relative">
                                            <div className="input-icon-left ms-1">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                    <rect x="2.25" y="4.5" width="13.5" height="9.75" rx="1.5" stroke="#9DACBD" stroke-width="1.5" />
                                                    <path d="M5.25 11.25H5.2575" stroke="#9DACBD" stroke-width="1.5" stroke-linecap="round" />
                                                    <path d="M3 8.25H15.75" stroke="#9DACBD" stroke-width="1.5" stroke-linecap="round" />
                                                </svg>
                                            </div>
                                            <input
                                                type="text"
                                                className="trial-modal-input form-control"
                                                placeholder="0000 0000 0000 0000"
                                                value={formData.cardNumber}
                                                onChange={(e) => handleInputChange("cardNumber", e.target.value)}
                                            />
                                        </div>
                                        {errors.cardNumber && <small className="text-danger">{errors.cardNumber}</small>}
                                    </div>

                                    <div className="w-100 d-flex gap-3 mb-0">
                                        <div className="flex-grow-1">
                                            <label className="form-label">Vencimiento (MM/AA)</label>
                                            <input
                                                type="text"
                                                className="trial-modal-input form-control"
                                                placeholder="MM/AA"
                                                value={formData.expiration}
                                                onChange={handleExpirationChange}
                                                maxLength={5}
                                            />
                                            {errors.expiration && <small className="text-danger">{errors.expiration}</small>}
                                        </div>
                                        <div className="flex-grow-1">
                                            <label className="form-label">CVC</label>
                                            <div className="trial-modal-input-icon position-relative">
                                                <div className="input-icon-left ms-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 13 15" fill="none">
                                                        <path d="M0.708496 7.79036C0.708496 6.45472 0.708496 5.7869 1.12343 5.37196C1.53836 4.95703 2.20618 4.95703 3.54183 4.95703H9.2085C10.5441 4.95703 11.212 4.95703 11.6269 5.37196C12.0418 5.7869 12.0418 6.45472 12.0418 7.79036V9.20703C12.0418 11.2105 12.0418 12.2122 11.4194 12.8346C10.797 13.457 9.7953 13.457 7.79183 13.457H4.95849C2.95503 13.457 1.95329 13.457 1.33089 12.8346C0.708496 12.2122 0.708496 11.2105 0.708496 9.20703V7.79036Z" stroke="#9DACBD" stroke-width="1.41667" />
                                                        <path d="M9.20817 4.2487V3.54037C9.20817 1.97556 7.93964 0.707031 6.37484 0.707031C4.81003 0.707031 3.5415 1.97556 3.5415 3.54037V4.2487" stroke="#9DACBD" stroke-width="1.41667" stroke-linecap="round" />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="trial-modal-input form-control"
                                                    placeholder="123"
                                                    value={formData.cvc}
                                                    onChange={(e) => handleInputChange("cvc", e.target.value)}
                                                />
                                            </div>
                                            {errors.cvc && <small className="text-danger">{errors.cvc}</small>}
                                        </div>
                                    </div>
                                    <div className="trial-summary-validation d-flex p-3">
                                        <span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                <circle cx="7" cy="7" r="5.25" stroke="#3177BE" stroke-width="1.16667" />
                                                <path d="M7.29171 4.3776C7.29171 4.53869 7.16112 4.66927 7.00004 4.66927C6.83896 4.66927 6.70837 4.53869 6.70837 4.3776C6.70837 4.21652 6.83896 4.08594 7.00004 4.08594C7.16112 4.08594 7.29171 4.21652 7.29171 4.3776Z" fill="#3177BE" stroke="#3177BE" stroke-width="0.583333" />
                                                <path d="M7 9.91927V5.83594" stroke="#3177BE" stroke-width="1.16667" />
                                            </svg>
                                        </span>
                                        <div className="trial-summary-validation-text m-0">
                                            Validación de seguridad: Durante el período de prueba gratuita no se efectuará ningún cobro. Se realizará un cargo temporal mínimo, totalmente reembolsable, con el fin de validar la autenticidad de la tarjeta.
                                        </div>
                                    </div>
                                </div>

                                <div className="trial-modal-card">
                                    <h5 className="trial-modal-card-title mb-0 d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="trial-modal-checkbox"
                                            id="invoice-checkbox"
                                            checked={formData.showInvoice}
                                            onChange={(e) => handleInputChange("showInvoice", e.target.checked)}
                                        />
                                        Quiero la factura electrónica de la orden
                                    </h5>
                                    {formData.showInvoice && (
                                        <div className="w-100 d-flex flex-column gap-3">

                                            <div className="w-100">
                                                <label className="form-label">NIT / Número de identificación Tributaria</label>
                                                <input
                                                    type="text"
                                                    className="trial-modal-input form-control"
                                                    placeholder="Ej. 900.123.456-7"
                                                    value={formData.nit}
                                                    onChange={(e) => handleInputChange("nit", e.target.value)}
                                                />
                                                {errors.nit && <small className="text-danger">{errors.nit}</small>}
                                            </div>
                                            <div className="w-100 d-flex gap-3">


                                                <div className="flex-grow-1 w-50">
                                                    <label className="form-label">Régimen Fiscal</label>

                                                    <Dropdown className="w-100 trial-modal-select">
                                                        <Dropdown.Toggle
                                                            className="w-100 d-flex justify-content-between align-items-center trial-modal-select-btn"
                                                        >
                                                            {
                                                                TAX_REGIME_OPTIONS.find(d => d.value === formData.fiscalRegime)?.label
                                                                || "Seleccionar ..."
                                                            }
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
                                                                <path d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z" fill="#9DACBD" />
                                                            </svg>
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu className="trial-modal-select-menu w-100">
                                                            {TAX_REGIME_OPTIONS.map(opt => (
                                                                <Dropdown.Item
                                                                    key={opt.value}
                                                                    onClick={() => handleInputChange("fiscalRegime", opt.value)}
                                                                >
                                                                    {opt.label}
                                                                </Dropdown.Item>
                                                            ))}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    {errors.fiscalRegime && <small className="text-danger">{errors.fiscalRegime}</small>}
                                                </div>


                                                <div className="flex-grow-1 w-50">
                                                    <label className="form-label">Responsabilidad Fiscal</label>

                                                    <Dropdown className="w-100 trial-modal-select">
                                                        <Dropdown.Toggle
                                                            className="w-100 d-flex justify-content-between align-items-center trial-modal-select-btn"
                                                        >
                                                            {
                                                                TAX_RESPONSIBILITY_OPTIONS.find(d => d.value === formData.fiscalResponsibility)?.label
                                                                || "Seleccionar ..."
                                                            }
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
                                                                <path d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z" fill="#9DACBD" />
                                                            </svg>
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu className="trial-modal-select-menu w-100">
                                                            {TAX_RESPONSIBILITY_OPTIONS.map(opt => (
                                                                <Dropdown.Item
                                                                    key={opt.value}
                                                                    onClick={() => handleInputChange("fiscalResponsibility", opt.value)}
                                                                >
                                                                    {opt.label}
                                                                </Dropdown.Item>
                                                            ))}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    {errors.fiscalResponsibility && <small className="text-danger">{errors.fiscalResponsibility}</small>}
                                                </div>
                                            </div>

                                            <div className="w-100">
                                                <label className="form-label">Correo para la facturación</label>

                                                <div className="trial-modal-input-icon position-relative">
                                                    <div className="input-icon-left">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <rect x="4" y="6" width="16" height="12" rx="2" stroke="#9DACBD" stroke-width="2" />
                                                            <path d="M4 9L11.1056 12.5528C11.6686 12.8343 12.3314 12.8343 12.8944 12.5528L20 9" stroke="#9DACBD" stroke-width="2" />
                                                        </svg>
                                                    </div>

                                                    <input
                                                        type="email"
                                                        className="trial-modal-input form-control "
                                                        placeholder="contabilidad@tuempresa.com"
                                                        value={formData.invoiceEmail}
                                                        onChange={(e) => handleInputChange("invoiceEmail", e.target.value)}
                                                    />
                                                    {errors.invoiceEmail && <small className="text-danger">{errors.invoiceEmail}</small>}
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                </div>

                            </div>

                            <div className="col-12 col-lg-5 d-flex flex-column gap-4 p-4 pt-0">
                                <div className="trial-modal-summary-card d-flex flex-column gap-3 pt-4">
                                    <h4 className="trial-summary-title mb-0 px-4">
                                        Resumen de tu Orden
                                    </h4>
                                    <p className="trial-summary-subtitle mb-0 px-4">
                                        Revisa los detalles antes de confirmar.
                                    </p>
                                    <div className="trial-summary-divider px-4" />
                                    <div className="trial-summary-plan d-flex justify-content-between align-items-center px-4 my-2">
                                        <div>
                                            <div className="trial-summary-plan-name">
                                                {selectedPlan.frontendPlan.name}
                                            </div>
                                            <div className="trial-summary-plan-cycle">
                                                Facturación {selectedPlan.billingPeriod === "yearly" ? "Anual" : "Mensual"}
                                            </div>
                                        </div>

                                        {isTrial && (
                                            <span className="trial-summary-badge">30 DÍAS GRATIS</span>
                                        )}
                                    </div>
                                    <div className="trial-summary-divider" />
                                    <div className="d-flex flex-column gap-2 px-4">
                                        <div className="d-flex justify-content-between">
                                            <span className="trial-summary-plan-cycle">
                                                Atletas ({selectedPlan.backendPlan.clients_limit} incluidos)
                                            </span>

                                            <strong className="trial-summary-plan-cycle">
                                                {totalUsers}
                                            </strong>
                                        </div>

                                        <div className="d-flex justify-content-between">
                                            <span className="trial-summary-plan-cycle">
                                                Admins ({selectedPlan.backendPlan.role_user_limit} incluidos)
                                            </span>

                                            <strong className="trial-summary-plan-cycle">
                                                {totalAdmins}
                                            </strong>
                                        </div>

                                        {!couponResult && (
                                            <div className="d-flex flex-row justify-content-between gap-3">
                                                <input
                                                    type="text"
                                                    className="form-control trial-summary-coupon-input w-100"
                                                    placeholder="Código de cupón"
                                                    value={formData.couponCode}
                                                    onChange={(e) => handleInputChange("couponCode", e.target.value)}
                                                />

                                                <button
                                                    className="trial-summary-apply-btn px-4"
                                                    onClick={handleApplyCoupon}
                                                >
                                                    Aplicar
                                                </button>
                                            </div>
                                        )}

                                        {couponError && (
                                            <small className="text-danger px-1">{couponError}</small>
                                        )}
                                    </div>
                                    {couponResult && (
                                        <>
                                            <div className="trial-summary-divider" />
                                            <div className="d-flex flex-column gap-2 px-4">
                                                <div className="trial-summary-coupon-success px-1 d-flex flex-row justify-content-between">
                                                    <div className="d-flex flex-row align-items-center gap-1">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M6.9115 1.68555L7.01111 1.7002L7.10779 1.72852C7.31823 1.80905 7.4322 1.97718 7.48474 2.09473C7.53886 2.21583 7.55683 2.34057 7.5658 2.42383C7.5843 2.59591 7.58337 2.83013 7.58337 3.07422V5.36621H8.09119C8.48249 5.36621 8.84262 5.36475 9.12244 5.40625C9.37593 5.44385 9.67792 5.53264 9.89783 5.78613L9.98669 5.90527L10.058 6.03613C10.1955 6.34211 10.1489 6.65366 10.0765 6.89941C9.99664 7.17073 9.84344 7.49655 9.67908 7.85156L8.08533 11.293C7.98278 11.5145 7.88536 11.7276 7.79626 11.876C7.75315 11.9478 7.68437 12.0534 7.58435 12.1406C7.47334 12.2373 7.26175 12.36 6.98865 12.2998C6.71588 12.2396 6.5761 12.0396 6.51599 11.9053C6.46186 11.7842 6.44293 11.6594 6.43396 11.5762C6.41545 11.4041 6.41638 11.1699 6.41638 10.9258V8.63379H5.90955C5.51816 8.63379 5.15719 8.63526 4.87732 8.59375C4.58765 8.55074 4.23435 8.44102 4.01306 8.09473C3.79179 7.74832 3.84148 7.38153 3.92419 7.10059C4.0041 6.82933 4.15638 6.50333 4.32068 6.14844L5.91443 2.70703C6.01698 2.48552 6.1144 2.27241 6.20349 2.12402C6.2466 2.05224 6.31533 1.94661 6.41541 1.85938C6.51249 1.77475 6.68655 1.67013 6.9115 1.68555Z" stroke="#6FD195" stroke-width="1.16667" />
                                                        </svg>
                                                        <span>Descuento ({couponResult?.code})</span>
                                                    </div>
                                                    <span>
                                                        - $
                                                        {(
                                                            (selectedPlan.totalPriceNumeric * Number(couponResult?.percentage || 0)) / 100
                                                        ).toLocaleString("es-CO")} ({couponResult?.percentage || 0}%)
                                                    </span>
                                                </div>
                                                <div className="trial-summary-coupon-helper px-1 d-flex flex-row align-items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                                        <path d="M3.125 8.75L4.85809 10.0498C5.28678 10.3713 5.89272 10.2978 6.23205 9.88305L11.25 3.75" stroke="#6FD195" stroke-width="2" stroke-linecap="round" />
                                                    </svg>
                                                    <span>Cupón aplicado a tu próximo pago</span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="trial-summary-divider" />
                                    <div className="trial-summary-total px-4">
                                        <div className="d-flex flex-row justify-content-between">
                                            <span>{isTrial ? "Total a pagar hoy" : "Total a pagar"}</span>
                                            <div className="d-flex flex-column align-items-end">
                                                <strong className="trial-summary-total-price">
                                                    {isTrial ? "$0 COP" : displayPrice}
                                                </strong>
                                                {isTrial && (
                                                    <div className="trial-summary-after">
                                                        Después de 30 días:{" "}
                                                        <strong>
                                                            {displayPrice}{" "}
                                                            / {selectedPlan.billingPeriod === "yearly" ? "año" : "mes"}
                                                        </strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="trial-summary-divider" />
                                    <div className="trial-summary-btn-container d-flex justify-content-center flex-column gap-3 p-4">
                                        <div className="px-4">
                                            <button
                                                className="trial-summary-btn w-100 px-4 py-3"
                                                onClick={handleConfirm}
                                                disabled={isSubmitting}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
                                                    <path d="M1 11C1 9.11438 1 8.17157 1.58579 7.58579C2.17157 7 3.11438 7 5 7H13C14.8856 7 15.8284 7 16.4142 7.58579C17 8.17157 17 9.11438 17 11V13C17 15.8284 17 17.2426 16.1213 18.1213C15.2426 19 13.8284 19 11 19H7C4.17157 19 2.75736 19 1.87868 18.1213C1 17.2426 1 15.8284 1 13V11Z" stroke="white" stroke-width="2" />
                                                    <path d="M13 6V5C13 2.79086 11.2091 1 9 1C6.79086 1 5 2.79086 5 5V6" stroke="white" stroke-width="2" stroke-linecap="round" />
                                                </svg>
                                                {isSubmitting
                                                    ? "Procesando..."
                                                    : isTrial
                                                        ? "Iniciar mi prueba gratuita"
                                                        : "Confirmar y pagar"
                                                }
                                            </button>
                                        </div>
                                        <p className="trial-summary-note d-flex flex-row gap-2 align-items-center m-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9" fill="none">
                                                <path d="M4.2041 0.84375C4.39276 0.762895 4.60724 0.762895 4.7959 0.84375L7.0166 1.7959C7.32032 1.92606 7.50329 2.23995 7.4668 2.56836L7.22266 4.7666C7.1477 5.44033 6.81472 6.05919 6.29395 6.49316L4.98047 7.58789C4.70233 7.81967 4.29767 7.81967 4.01953 7.58789L2.70605 6.49316C2.18528 6.05919 1.8523 5.44033 1.77734 4.7666L1.5332 2.56836C1.49671 2.23994 1.67968 1.92606 1.9834 1.7959L4.2041 0.84375Z" stroke="white" stroke-width="0.75" stroke-linecap="round" />
                                            </svg>
                                            Pagos procesados de forma segura con encriptación SSL de 256 bits.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ActivateTrialModal;
